import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Grid,
  Button,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import {
  CheckCircle as CheckIcon,
  NavigateNext as NextIcon,
  Crop as CropIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import ImageCropperDialog from "./ImageCropperDialog";
import IconHelper from "../Helpers/IconHelper";
import ColorHelper from "../Helpers/ColorHelper";
import "../Components/Ui-Components/SectionTitle2/SectionTitle2.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Custom theme for tooltip
const customTheme = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#FFFFFF",
          color: "black",
          border: "1px solid #e0e0e0",
        },
      },
    },
  },
});

const EventImageProcessor = ({
  eventFields,
  processedImages,
  processingIndex,
  onImageProcessed,
  onNavigateToCertificate,
  onClose,
  templateDetails,
  onBackToImageSelection,
  handleEditForm, // New prop
}) => {
  const [cropperOpen, setCropperOpen] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [imageLoadingStates, setImageLoadingStates] = useState({});
  const [imageErrors, setImageErrors] = useState({});
  const [forceRefresh, setForceRefresh] = useState(0);

  const FontHelper = {
    fSemiBold: "inherit",
    fBold: "inherit",
  };

  // Progress calculation
  const totalImages = eventFields.length;
  const completedImages = processedImages.filter((img) => img !== null).length;
  const progressPercentage = (completedImages / totalImages) * 100;

  // Placeholder: get event name
  const getEventNameById = (eventId) => {
    return eventId;
  };

  // Extract required_sequence
  const requiredSequence = templateDetails?.template?.config?.required_sequence || [];

  useEffect(()=>{
    console.log("Required Sequence:", requiredSequence);
  })

  // Sort eventFields based on required_sequence
  const sortedEventFields = [...eventFields].sort((a, b) => {
    const aIndex = requiredSequence.findIndex((seq) => seq.event_id === a.eventId);
    const bIndex = requiredSequence.findIndex((seq) => seq.event_id === b.eventId);
    // If not in required_sequence, push to end
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });

  // Open cropper
  const handleOpenCropper = (imageIndex) => {
    const eventField = sortedEventFields[imageIndex];
    if (eventField && eventField.selectedImage) {
      const lastImage =
        eventField.selectedImage.high_res_url ||
        processedImages[eventFields.indexOf(eventField)]?.processedImageUrl ||
        eventField.selectedImage.s3;
      setCurrentImageIndex(eventFields.indexOf(eventField));
      setCurrentImageUrl(lastImage);
      setCropperOpen(true);
    }
  };

  const handleCroppedImage = (imageUrl, cropData) => {
    setCropperOpen(false);

    if (imageUrl) {
      // Store both the image URL and crop data
      const processedData = {
        processedImageUrl: imageUrl,
        cropData: cropData,
      };

      // Clear any previous errors for this image
      setImageErrors(prev => ({ ...prev, [currentImageIndex]: false }));

      // Clear loading state for this image
      setImageLoadingStates(prev => ({ ...prev, [currentImageIndex]: false }));

      // Call the parent callback
      onImageProcessed(processedData, currentImageIndex);

      // Force a refresh to ensure UI updates
      setTimeout(() => {
        setForceRefresh(prev => prev + 1);
      }, 100);
    }
  };

  // Preview image
  const handlePreviewImage = (imageUrl) => {
    setPreviewImageUrl(imageUrl);
    setPreviewOpen(true);
  };

  // Check if all images are processed
  const isAllProcessed = processedImages.every((img) => img !== null);

  // Monitor changes to processedImages (reduced logging)
  useEffect(() => {
    const processedCount = processedImages.filter(img => img !== null).length;
  }, [processedImages]);

  return (
    <ThemeProvider theme={customTheme}>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header Section  */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 2,
          }}
        >
          {/* Go Back Circular Button */}
          <Box
            onClick={onBackToImageSelection}
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              width: 36,
              height: 36,
              mr: 2,
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "#E7E0F5",
              },
            }}
          >
            <IconHelper.LeftArrow />
          </Box>
          {/* Left Title */}
          <Typography
            sx={{
              // fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
              fontSize: { xs: 16, sm: 18, md: 20 },
              fontWeight: 600,
              color: "#3F0E77",
              fontFamily: "inherit",
              mr: 3,
            }}
          >
            Process Your Images
          </Typography>

          {/* Divider Line */}
          <Box
            sx={{
              flexGrow: 1,
              height: "1px",
              backgroundColor: "#E0DFF5",
              mr: 2,
            }}
          />

          {/* Right Circular Progress + Tooltip */}
          <Box sx={{ display: "flex", alignItems: "center", ml: 2 }}>
            <Tooltip
              title={
                <Box
                  sx={{
                    p: 1,
                    maxHeight: 160,
                    maxWidth: 250,
                    overflowY: "auto",
                    bgcolor: "#FFFFFF",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.3,
                    "@media (max-width:430px)": {
                      maxHeight: 120,
                      maxWidth: 180,
                      p: 0.5,
                    },
                  }}
                >
                  {sortedEventFields.map((field) => {
                    const chipKey = `${field.eventId}-${field.bibNumber}`;
                    return (
                      <Chip
                        key={chipKey}
                        label={`${getEventNameById(field.eventId)} - BIB: ${field.bibNumber}`}
                        variant={processedImages[eventFields.indexOf(field)] ? "filled" : "outlined"}
                        icon={
                          processedImages[eventFields.indexOf(field)] ? (
                            <CheckIcon />
                          ) : (
                            <CropIcon />
                          )
                        }
                        size="small"
                        sx={{
                          m: 0.2,
                          fontSize: "0.72rem",
                          height: 22,
                          color: processedImages[eventFields.indexOf(field)] ? "#FFFFFF" : "default",
                          bgcolor: processedImages[eventFields.indexOf(field)] ? "#3F0E77" : "default",
                          "& .MuiChip-icon": {
                            fontSize: "1rem",
                            color: processedImages[eventFields.indexOf(field)] ? "#FFFFFF" : "inherit",
                          },
                          "@media (max-width:430px)": {
                            fontSize: "0.65rem",
                            height: 20,
                            "& .MuiChip-icon": { fontSize: "0.9rem" },
                          },
                        }}
                      />

                    );
                  })}
                </Box>
              }
              placement="left"
              enterTouchDelay={0}
              leaveTouchDelay={3000}
            >
              <Box
                sx={{
                  position: "relative",
                  width: 60,
                  height: 60,
                  bgcolor: "#E8F5E9",
                  borderRadius: "50%",
                }}
              >
                <CircularProgress
                  variant="determinate"
                  value={progressPercentage}
                  size={60}
                  thickness={4}
                  sx={{
                    color: "#3F0E77",
                    "& .MuiCircularProgress-circle": { strokeLinecap: "round" },
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "black",
                    fontWeight: "bold",
                  }}
                >
                  <Typography variant="body2" fontSize="medium" align="center">
                    {`${completedImages}/${totalImages}`}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            minHeight: "auto",
            marginBottom: "20px"
          }}
        >
          <Box
            sx={{
              width: "100vw",
              padding: { xs: "10px 5px", lg: "8px 5px" },
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              height: 'auto',
              backgroundColor: ColorHelper.LightPurple,
              color: ColorHelper.BlueColor,
              fontFamily: FontHelper.fRegular,
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            Crop and resize your selected images for the certificate
          </Box>
        </Box>

        {/* Image Processing Grid */}
        <Grid container spacing={3} sx={{ marginBottom: '80px' }}>
          {sortedEventFields.map((field, index) => {
            const originalIndex = eventFields.indexOf(field);

            // Prioritize cropped/processed image over original
            const processedImage = processedImages[originalIndex];
            let chosenImage;

            if (processedImage && processedImage.processedImageUrl) {
              // Use the cropped/processed image if available
              chosenImage = processedImage.processedImageUrl;
            } else {
              // Fallback to high-res or s3 image
              chosenImage = field.selectedImage.high_res_url || field.selectedImage.s3;
            }

            if (chosenImage === field.selectedImage.high_res_url) {
            }
            const cardKey = `${field.eventId}-${field.bibNumber}`;

            return (
              <Grid item xs={12} md={6} lg={4} key={cardKey}>
                <Card
                  elevation={2}
                  sx={{
                    height: "100%",
                    border: processedImages[originalIndex]
                      ? "2px solid #3F0E77"
                      : "1px solid #e0e0e0",
                  }}
                >
                  <CardContent sx={{ pb: 2, pt: 1 }}>
                    {/* Event Info */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1.5,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "bold" }}
                        >
                          {getEventNameById(field.eventId)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          BIB: {field.bibNumber}
                        </Typography>
                      </Box>
                      {processedImages[originalIndex] && (
                        <CheckIcon
                          sx={{ color: "#3F0E77", fontSize: "2rem" }}
                        />
                      )}
                    </Box>

                    {/* Single Image Slot */}
                    <Box sx={{ position: "relative", mb: 1.5 }}>
                      {imageLoadingStates[originalIndex] && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "rgba(0,0,0,0.1)",
                            borderRadius: 2,
                            zIndex: 2,
                          }}
                        >
                          <CircularProgress size={40} />
                        </Box>
                      )}
                      <img
                        key={`img-${originalIndex}-${forceRefresh}-${processedImage ? 'processed' : 'original'}`}
                        src={chosenImage}
                        alt={`Event ${index + 1}`}
                        crossOrigin={chosenImage.startsWith('data:') || chosenImage.startsWith('blob:') ? undefined : "anonymous"}
                        style={{
                          width: "100%",
                          height: 200,
                          objectFit: "cover",
                          borderRadius: 8,
                          display: imageErrors[originalIndex] ? 'none' : 'block',
                        }}
                        onLoad={(e) => {
                          setImageLoadingStates(prev => ({ ...prev, [originalIndex]: false }));
                          setImageErrors(prev => ({ ...prev, [originalIndex]: false }));
                        }}
                        onLoadStart={() => {
                          setImageLoadingStates(prev => ({ ...prev, [originalIndex]: true }));
                        }}
                        onError={(e) => {
                          setImageLoadingStates(prev => ({ ...prev, [originalIndex]: false }));
                          setImageErrors(prev => ({ ...prev, [originalIndex]: true }));

                          // If processed image fails, try the original
                          if (processedImage && chosenImage === processedImage.processedImageUrl) {
                            e.target.onerror = null;
                            e.target.src = field.selectedImage.high_res_url || field.selectedImage.s3;
                          }
                        }}
                      />

                      {/* Show error message if image failed to load */}
                      {imageErrors[originalIndex] && (
                        <Box
                          sx={{
                            width: "100%",
                            height: 200,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "#f5f5f5",
                            borderRadius: 2,
                            border: "2px dashed #ddd",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Image failed to load
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Action Buttons */}
                    <Box
                      sx={{ display: "flex", gap: 1, flexDirection: "column" }}
                    >
                      <Button
                        variant="contained"
                        onClick={() => handleOpenCropper(index)}
                        startIcon={<CropIcon />}
                        sx={{
                          bgcolor: "#3F0E77",
                          "&:hover": { bgcolor: "#2F0A5A" },
                        }}
                        fullWidth
                      >
                        {processedImages[originalIndex]
                          ? "Edit Image"
                          : "Crop & Resize"}
                      </Button>
                    </Box>

                    {/* Status */}
                    <Box sx={{ mt: 1, mb: 0, textAlign: "center" }}>
                      {processedImages[originalIndex] ? (
                        <Typography variant="body2" color="#3F0E77">
                          Image Cropped & Saved Successfully!
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="#3F0E77">
                          Waiting for processing
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Generate Certificate Button */}
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100%",
            bgcolor: "#FFFFFF",
            borderTop: "1px solid #E0E0E0",
            py: 2,
            px: { xs: 2, sm: 4, md: 6 },
            display: "flex",
            justifyContent: "flex-end",
            zIndex: 999,
          }}
        >
          <Button
            variant="contained" // use contained to get filled background
            startIcon={<NextIcon />}
            onClick={() => {
              if (isAllProcessed) {
                onNavigateToCertificate();
              } else {
                toast.error("Resize image(s) to preview the certificate", {
                  position: "top-right",
                  autoClose: 3000,
                  hideProgressBar: false, 
                  progressStyle: { background: "#3F0E77" },
                  style: {
                    backgroundColor: "#FFFFFF",
                    color: "#3F0E77",          
                  },
                });

              }
            }}
            size="large"
            sx={{
              bgcolor: "#3F0E77",
              color: "#FFFFFF",
              textTransform: "none",
              borderRadius: 1,
              fontSize: { xs: "0.70rem", sm: "0.875rem", md: "1rem" },
              padding: { xs: "6px 12px", sm: "8px 16px", md: "10px 20px" },
              "& .MuiButton-startIcon": {
                marginRight: { xs: 0.5, sm: 1 },
                "& .MuiSvgIcon-root": {
                  fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
                },
              },
              "&:hover": {
                bgcolor: "#2F0A5A", // slightly darker on hover
                color: "#FFFFFF",
              },
              minWidth: { xs: "auto", sm: "120px" },
            }}
          >
            Generate Certificate
          </Button>

        </Box>

        {/* Image Cropper Dialog */}
        <ImageCropperDialog
          open={cropperOpen}
          onClose={() => setCropperOpen(false)}
          image={currentImageUrl}
          onCroppedImage={handleCroppedImage}
        />

        {/* Preview Dialog */}
        <Dialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          maxWidth="md"
          fullWidth
          keepMounted
          disableScrollLock
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
        >
          <DialogTitle>
            Image Preview
            <IconButton
              onClick={() => setPreviewOpen(false)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
              }}
            >
              ×
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {previewImageUrl && (
              <img
                src={previewImageUrl}
                alt="Preview"
                style={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "70vh",
                  objectFit: "contain",
                }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setPreviewOpen(false)}
              sx={{ color: "#3F0E77", border: "1px solid #3F0E77" }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
      <ToastContainer />
    </ThemeProvider>
  );
};

export default EventImageProcessor;